import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api/client.js';
import { toastError } from '../../components/ui/Toast.js';
import { Icon } from '../../components/ui/Icon.js';
import { Badge } from '../../components/ui/Primitives.js';
import { formatDuration } from '../../lib/format.js';

/**
 * A call's recording, drawn and played (owner, 22 September 2026: "use a wave form to show the
 * channels and playback of the recording").
 *
 * One lane per channel. The switch records the call's first leg in stereo: what that leg heard
 * from its own party on the left, and the other side on the right (RECORD_STEREO in agent
 * fsxml.ts, switch_ivr_async.c) -- so the lanes are the call's From and To, and who spoke when
 * is visible before anything is played.
 *
 * The file is fetched once, through the same signed, five-minute, this-browser-only link as
 * before, and everything else is done here: the lanes are drawn from the decoded audio, playback
 * is from the fetched bytes (so a seek never needs a fresh link), and the bytes are hashed and
 * compared with the digest the node recorded when it uploaded them. Nothing new is stored.
 */

const RECORDING_BASE = '/api/v1/recordings/';
/** Beyond this the lanes are not drawn: decoding an hour of audio is a lot to ask of a browser. */
const MAX_DRAW_BYTES = 25 * 1024 * 1024;
const SPEEDS = [1, 1.25, 1.5, 2] as const;

type Integrity = 'checking' | 'match' | 'mismatch' | 'none';

interface Loaded {
  url: string;
  /** Peak per bar, 0..1, one array per channel. */
  lanes: number[][];
  duration: number;
  integrity: Integrity;
}

async function sha256Hex(buf: ArrayBuffer): Promise<string> {
  const d = await crypto.subtle.digest('SHA-256', buf);
  return [...new Uint8Array(d)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** Peaks for `bars` bars across the whole file, per channel. */
function peaks(audio: AudioBuffer, bars: number): number[][] {
  const out: number[][] = [];
  for (let ch = 0; ch < audio.numberOfChannels; ch++) {
    const data = audio.getChannelData(ch);
    const step = Math.max(1, Math.floor(data.length / bars));
    const lane: number[] = [];
    let max = 0;
    for (let b = 0; b < bars; b++) {
      let p = 0;
      const end = Math.min(data.length, (b + 1) * step);
      for (let i = b * step; i < end; i++) {
        const v = Math.abs(data[i]!);
        if (v > p) p = v;
      }
      lane.push(p);
      if (p > max) max = p;
    }
    // Scaled to the loudest point of the call, so a quiet line still shows its speech.
    out.push(max > 0 ? lane.map((v) => v / max) : lane);
  }
  return out;
}

const BARS = 240;

export function RecordingPlayer({
  tenantId, callId, status, laneNames, expectedSha256, format, bytes, onTime, seekTo,
}: {
  tenantId: string; callId: string;
  status: 'ready' | 'saving' | 'unavailable';
  /** Left then right: who is on each channel. */
  laneNames: [string, string];
  expectedSha256: string | null;
  format: string | null;
  bytes: number | null;
  /**
   * Where the playhead is, in seconds, for a transcript beside the player to follow (docs/37 §6:
   * "the current utterance highlighted").
   */
  onTime?: ((sec: number) => void) | undefined;
  /**
   * A request to play from a moment -- a transcript line that was clicked. `n` changes on every
   * request, so clicking the same line twice plays it twice.
   */
  seekTo?: { sec: number; n: number } | null | undefined;
}) {
  const audio = useRef<HTMLAudioElement | null>(null);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [at, setAt] = useState(0);
  const [speed, setSpeed] = useState<(typeof SPEEDS)[number]>(1);

  const link = async (download: boolean): Promise<string | null> => {
    try {
      const r = await api.post<{ token: string }>('/v1/recordings/link',
        { tenant_id: tenantId, cdr_id: callId, download });
      return RECORDING_BASE + r.token;
    } catch (e) {
      toastError(e);
      return null;
    }
  };

  const load = async (): Promise<void> => {
    setBusy(true); setError(null);
    try {
      const url = await link(false);
      if (!url) return;
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`The recording could not be fetched (${res.status}).`);
      const buf = await res.arrayBuffer();
      const type = res.headers.get('content-type') ?? 'audio/mpeg';

      const hash = expectedSha256 ? sha256Hex(buf) : Promise.resolve(null);
      let lanes: number[][] = [];
      let duration = 0;
      if (buf.byteLength <= MAX_DRAW_BYTES) {
        // Decoded at telephone rate: the call was 8 kHz, and a 48 kHz decode is six times the memory.
        const ctx = new OfflineAudioContext(1, 1, 8000);
        const decoded = await ctx.decodeAudioData(buf.slice(0));
        lanes = peaks(decoded, BARS);
        duration = decoded.duration;
      }
      const digest = await hash;
      setLoaded({
        url: URL.createObjectURL(new Blob([buf], { type })),
        lanes, duration,
        integrity: !expectedSha256 ? 'none' : digest === expectedSha256 ? 'match' : 'mismatch',
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The recording could not be loaded.');
    } finally {
      setBusy(false);
    }
  };

  // The fetched bytes are held as an object URL; let them go with the player.
  useEffect(() => () => { if (loaded) URL.revokeObjectURL(loaded.url); }, [loaded]);

  /**
   * Fetched and drawn as the call opens, so what the call sounded like is on screen at once; the
   * round button plays it. Once per player (a ref, because a dev build mounts effects twice).
   */
  const started = useRef(false);
  useEffect(() => {
    if (status !== 'ready' || started.current) return;
    started.current = true;
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => { if (audio.current) audio.current.playbackRate = speed; }, [speed]);

  // The playhead, smoothly: timeupdate fires only a few times a second.
  useEffect(() => {
    if (!playing) return;
    let raf = 0;
    const tick = () => { if (audio.current) setAt(audio.current.currentTime); raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing]);

  useEffect(() => { onTime?.(at); }, [at, onTime]);

  // A line clicked in the transcript plays the call from there.
  useEffect(() => {
    const a = audio.current;
    if (!seekTo || !a || !loaded) return;
    a.currentTime = Math.max(0, seekTo.sec);
    setAt(a.currentTime);
    a.playbackRate = speed;
    void a.play();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seekTo?.n, loaded]);

  const duration = loaded?.duration || audio.current?.duration || 0;

  const seek = useCallback((fraction: number) => {
    const a = audio.current;
    if (!a || !Number.isFinite(a.duration)) return;
    a.currentTime = Math.max(0, Math.min(1, fraction)) * a.duration;
    setAt(a.currentTime);
  }, []);

  const download = async (): Promise<void> => {
    const url = await link(true);
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.click();
  };

  const about = [
    format ? (format.startsWith('mp3') ? 'MP3' : 'WAV') : null,
    bytes ? `${Math.max(1, Math.round(bytes / 1024))} KB` : null,
  ].filter(Boolean).join(' · ');

  const played = duration > 0 ? at / duration : 0;
  const toggle = () => {
    const a = audio.current;
    if (!a) return;
    if (a.paused) { a.playbackRate = speed; void a.play(); } else a.pause();
  };
  const nextSpeed = () => setSpeed(SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length]!);
  const lanes = loaded?.lanes ?? [];

  return (
    <section className="arc-rec" aria-label="Recording">
      <div className="arc-rec-head">
        <span className="arc-rec-title">Recording</span>
        {about && <span className="arc-subtle" style={{ fontSize: 12 }}>{about}</span>}
        <div className="arc-spacer" />
        {loaded && <IntegrityBadge state={loaded.integrity} />}
        {loaded && (
          <button type="button" className="arc-pill-btn arc-num" onClick={nextSpeed}
            aria-label={`Speed ${speed} times, press for the next`}>{speed}×</button>
        )}
        {status === 'ready' && (
          <button type="button" className="arc-pill-btn" onClick={() => void download()}>Download</button>
        )}
      </div>

      {status !== 'ready' ? (
        <p className="arc-subtle" style={{ margin: 0 }}>
          {status === 'saving'
            ? 'Being saved to storage. It can be played in a minute or two.'
            : 'Recorded, but the file is not in storage.'}
        </p>
      ) : error ? (
        <div className="arc-row" style={{ gap: 10 }}>
          <p className="arc-error" style={{ margin: 0 }}>{error}</p>
          <button type="button" className="arc-pill-btn" onClick={() => void load()}>Try again</button>
        </div>
      ) : (
        <div className="arc-player">
          {loaded && (
            <audio ref={audio} src={loaded.url} preload="auto"
              onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
              onEnded={() => { setPlaying(false); setAt(audio.current?.duration ?? 0); }}
              onTimeUpdate={() => { if (!playing && audio.current) setAt(audio.current.currentTime); }} />
          )}

          <div className="arc-player-main">
            {!loaded ? (
              <div className="arc-wave arc-wave-loading" aria-busy="true" aria-label="Loading the recording">
                <div className="arc-skeleton" /><div className="arc-skeleton" />
              </div>
            ) : lanes.length === 0 ? (
              <p className="arc-subtle" style={{ margin: 0, fontSize: 12 }}>Too long to draw; it plays as usual.</p>
            ) : (
              <Wave lanes={lanes} names={lanes.length === 1 ? ['Both parties'] : laneNames}
                played={played} duration={duration} onSeek={seek} />
            )}
            <div className="arc-player-time arc-num">
              <span>{formatDuration(Math.floor(at))}</span>
              <span className="arc-subtle">{loaded ? formatDuration(Math.round(duration)) : ''}</span>
            </div>
          </div>
          {/* On the right, where the owner wanted it (22 September 2026). */}
          <button type="button" className="arc-player-toggle" disabled={!loaded}
            aria-label={playing ? 'Pause' : 'Play'} onClick={toggle}>
            <Icon name={playing ? 'pause' : 'play'} size={18} />
          </button>
        </div>
      )}
    </section>
  );
}

function IntegrityBadge({ state }: { state: Integrity }) {
  if (state === 'match') {
    return <span title="The file played is byte for byte the one stored when the call ended."><Badge tone="ok">unaltered</Badge></span>;
  }
  if (state === 'mismatch') {
    return <span title="The file does not hash to the digest recorded at upload."><Badge tone="danger">Does not match its record</Badge></span>;
  }
  if (state === 'none') {
    return <span className="arc-subtle" style={{ fontSize: 12 }} title="Recorded before digests were kept (20 September 2026).">No digest to check</span>;
  }
  return null;
}

/** Each party in its own colour: the caller's side green, the other side blue. */
const LANE_COLOURS = ['--arc-accent', '--arc-dir-out'] as const;

/**
 * The channels, stacked, with one playhead across them. A click anywhere seeks; hovering shows
 * where a click would land, and when that is.
 */
function Wave({ lanes, names, played, duration, onSeek }: {
  lanes: number[][]; names: readonly string[]; played: number; duration: number;
  onSeek: (fraction: number) => void;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const at = (e: React.MouseEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
  };
  return (
    <div className="arc-wave" role="slider" tabIndex={0} aria-label="Position in the recording"
      aria-valuemin={0} aria-valuemax={Math.round(duration)} aria-valuenow={Math.round(played * duration)}
      aria-valuetext={formatDuration(Math.floor(played * duration))}
      onClick={(e) => onSeek(at(e))}
      onMouseMove={(e) => setHover(at(e))}
      onMouseLeave={() => setHover(null)}
      onKeyDown={(e) => {
        const step = duration > 0 ? 5 / duration : 0.05;
        if (e.key === 'ArrowRight') { e.preventDefault(); onSeek(played + step); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); onSeek(played - step); }
      }}>
      {lanes.map((lane, i) => (
        <Lane key={i} lane={lane} played={played} name={names[i] ?? `Channel ${i + 1}`}
          colour={LANE_COLOURS[i % LANE_COLOURS.length]!} />
      ))}
      <span className="arc-wave-head" style={{ left: `${played * 100}%` }} aria-hidden="true" />
      {hover !== null && (
        <span className="arc-wave-hover" style={{ left: `${hover * 100}%` }} aria-hidden="true">
          <span className="arc-wave-hover-time arc-num">{formatDuration(Math.floor(hover * duration))}</span>
        </span>
      )}
    </div>
  );
}

/** One channel: bars from its peaks, played in its colour and the rest faded. */
function Lane({ lane, played, name, colour }: {
  lane: number[]; played: number; name: string; colour: string;
}) {
  const canvas = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const c = canvas.current;
    if (!c) return;
    const draw = () => {
      const dpr = window.devicePixelRatio || 1;
      const w = c.clientWidth;
      const h = c.clientHeight;
      if (w === 0) return;
      c.width = Math.round(w * dpr);
      c.height = Math.round(h * dpr);
      const g = c.getContext('2d');
      if (!g) return;
      g.scale(dpr, dpr);
      g.fillStyle = getComputedStyle(c).getPropertyValue(colour).trim() || '#168550';
      const bw = w / lane.length;
      const mid = h / 2;
      lane.forEach((p, i) => {
        const bh = Math.max(1.5, p * (h - 6));
        g.globalAlpha = (i + 0.5) / lane.length <= played ? 1 : 0.28;
        const x = i * bw + bw * 0.18;
        const bwi = Math.max(1, bw * 0.64);
        g.beginPath();
        g.roundRect(x, mid - bh / 2, bwi, bh, Math.min(bwi / 2, 1.5));
        g.fill();
      });
    };
    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(c);
    return () => ro.disconnect();
  }, [lane, played, colour]);

  return (
    <div className="arc-wave-lane">
      <span className="arc-wave-name arc-truncate" style={{ color: `var(${colour})` }}>{name}</span>
      <canvas ref={canvas} className="arc-wave-canvas" aria-hidden="true" />
    </div>
  );
}
